<template>
  <div class="seal-coord-page">
    <van-nav-bar title="印章点位精准录入" left-arrow @click-left="$router.back()" />

    <div class="toolbar">
      <div class="form-row">
        <label>规则</label>
        <el-select v-model="selectedRuleCode" placeholder="请选择印章规则" filterable class="rule-select">
          <el-option
            v-for="rule in allRules"
            :key="rule.ruleCode"
            :label="(rule.remark || '') + ' (' + rule.ruleCode + ')'"
            :value="rule.ruleCode"
          />
        </el-select>
      </div>
      <div class="form-row">
        <label>坐标系</label>
        <el-radio-group v-model="coordinateSystem" class="cs-radio-group" :disabled="entryPopupVisible">
          <el-radio :label="1">固定区 800×600</el-radio>
          <el-radio :label="2">全屏 0–10000</el-radio>
        </el-radio-group>
      </div>
      <p class="cs-hint">{{ coordinateHint }}</p>
      <p class="tip">
        请先选好规则与坐标系，点击「开始录入」后在弹层内四指<strong>同时</strong>按下；松手后提交。2 秒内最多提交一次。
      </p>
      <van-button
        type="primary"
        block
        round
        class="start-btn"
        :disabled="!canStartEntry || saving"
        @click="openEntryPopup"
      >
        开始录入
      </van-button>
    </div>

    <!-- 录入弹层：坐标系 1 = 标准 800×600 区域；坐标系 2 = 整屏 -->
    <van-popup
      v-model="entryPopupVisible"
      :position="entryPopupPosition"
      :style="entryPopupOuterStyle"
      :close-on-click-overlay="false"
      get-container="body"
      :class="['entry-popup-root', { 'entry-popup--fullsheet': entryPopupVisible && !isActiveFixedCs }]"
      @closed="onEntryPopupClosed"
    >
      <div class="entry-shell" :class="isActiveFixedCs ? 'entry-shell--800' : 'entry-shell--full'">
        <div v-if="isActiveFixedCs" class="entry-hd">
          <span class="entry-title">标准区录入 800×600</span>
          <van-button type="default" size="small" plain hairline :disabled="saving" @click="closeEntryPopup">
            关闭
          </van-button>
        </div>
        <div
          ref="touchHost"
          class="touch-host"
          :class="isActiveFixedCs ? 'touch-host--800' : 'touch-host--full'"
          @touchstart.prevent="onTouchStart"
          @touchend.prevent="onTouchEnd"
          @touchcancel.prevent="onTouchEnd"
        >
          <!-- 全屏：无顶栏；触摸必须 .stop，否则父级 @touchstart.prevent 会吞掉移动端 click -->
          <div
            v-if="!isActiveFixedCs"
            class="entry-float-close-wrap"
            @touchstart.stop="onCloseBtnTouch"
            @touchend.stop="onCloseBtnTouch"
            @touchcancel.stop="onCloseBtnTouch"
            @click.stop="closeEntryPopup"
          >
            <van-button
              type="default"
              size="small"
              round
              hairline
              native-type="button"
              class="entry-float-close"
              :disabled="saving"
            >
              关闭
            </van-button>
          </div>
          <canvas ref="markCanvas" class="mark-canvas" />
          <div class="region-label" :class="{ 'region-label--cs2': !isActiveFixedCs }">
            {{ regionLabelText }}
          </div>
          <div v-if="pendingPreview.length === 4" class="preview-text">已采集 4 点，松手后提交</div>
        </div>
      </div>
    </van-popup>
  </div>
</template>

<script>
import { getSealRules, addSealPoints } from '@/api/sealTest'
import { Toast } from 'vant'

/** 标准区与线上一致：宽 800 高 600 */
const STD_W = 800
const STD_H = 600
/** 全屏模式归一化精度 */
const NORM = 10000

export default {
  name: 'SealCoordinateEntry',
  data() {
    return {
      allRules: [],
      selectedRuleCode: null,
      coordinateSystem: 1,
      /** 打开弹层时快照，录入过程中不随表单改动 */
      activeCoordinateSystem: 1,
      entryPopupVisible: false,
      pendingFour: null,
      pendingPreview: [],
      lastSubmitAt: 0,
      saving: false
    }
  },
  computed: {
    isFixedCoordinateSystem() {
      return Number(this.coordinateSystem) === 1
    },
    isActiveFixedCs() {
      return Number(this.activeCoordinateSystem) === 1
    },
    canStartEntry() {
      return !!(this.selectedRuleCode && String(this.selectedRuleCode).trim())
    },
    coordinateHint() {
      return this.isFixedCoordinateSystem
        ? '弹层为固定比例区域，映射逻辑坐标 800×600（宽×高）。'
        : '弹层占满屏幕，映射 0–10000。'
    },
    regionLabelText() {
      return this.isActiveFixedCs ? '在此区域内四指按下 · 逻辑 800×600' : '全屏四指按下 · 0–10000'
    },
    entryPopupPosition() {
      return this.isActiveFixedCs ? 'center' : 'bottom'
    },
    entryPopupOuterStyle() {
      if (this.isActiveFixedCs) {
        return { width: 'auto', maxWidth: '94vw', background: 'transparent' }
      }
      return {
        width: '100%',
        height: '100%',
        maxWidth: '100%',
        borderRadius: '0'
      }
    }
  },
  watch: {
    entryPopupVisible(val) {
      if (val) {
        this.$nextTick(() => this.resizeMarkCanvas())
      }
    }
  },
  mounted() {
    this.loadRules()
    window.addEventListener('resize', this.onWindowResize)
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.onWindowResize)
  },
  methods: {
    onWindowResize() {
      if (this.entryPopupVisible) {
        this.$nextTick(() => this.resizeMarkCanvas())
      }
    },
    openEntryPopup() {
      if (!this.canStartEntry) {
        Toast.fail('请先选择规则')
        return
      }
      this.activeCoordinateSystem = Number(this.coordinateSystem)
      this.pendingFour = null
      this.pendingPreview = []
      this.entryPopupVisible = true
    },
    closeEntryPopup() {
      if (this.saving) return
      this.entryPopupVisible = false
    },
    /** 仅用于阻止冒泡到 touch-host，避免父级 preventDefault 导致按钮无法触发 click */
    onCloseBtnTouch() {},
    onEntryPopupClosed() {
      this.pendingFour = null
      this.pendingPreview = []
      this.clearCanvas()
    },
    async loadRules() {
      try {
        const res = await getSealRules()
        const rules = res.data || res || []
        this.allRules = Array.isArray(rules) ? rules : []
      } catch (e) {
        console.error(e)
        Toast.fail('加载规则失败')
      }
    },
    resizeMarkCanvas() {
      const host = this.$refs.touchHost
      const cv = this.$refs.markCanvas
      if (!host || !cv) return
      const w = host.clientWidth
      const h = host.clientHeight
      if (w <= 0 || h <= 0) return
      const dpr = window.devicePixelRatio || 1
      cv.width = Math.round(w * dpr)
      cv.height = Math.round(h * dpr)
      cv.style.width = w + 'px'
      cv.style.height = h + 'px'
      const ctx = cv.getContext('2d')
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0)
      ctx.clearRect(0, 0, w, h)
    },
    clearCanvas() {
      const host = this.$refs.touchHost
      const cv = this.$refs.markCanvas
      if (!host || !cv) return
      const ctx = cv.getContext('2d')
      const w = host.clientWidth
      const h = host.clientHeight
      ctx.clearRect(0, 0, w, h)
    },
    drawFourMarks(touches, rect) {
      this.clearCanvas()
      const cv = this.$refs.markCanvas
      if (!cv || !touches || touches.length < 4) return
      const ctx = cv.getContext('2d')
      for (let i = 0; i < 4; i++) {
        const t = touches[i]
        const px = t.clientX - rect.left
        const py = t.clientY - rect.top
        ctx.fillStyle = '#e53935'
        ctx.beginPath()
        ctx.arc(px, py, 10, 0, Math.PI * 2)
        ctx.fill()
        ctx.fillStyle = '#fff'
        ctx.font = 'bold 12px sans-serif'
        ctx.textAlign = 'center'
        ctx.textBaseline = 'middle'
        ctx.fillText(String(i + 1), px, py)
      }
    },
    onTouchStart(e) {
      const host = this.$refs.touchHost
      if (!host) return
      if (e.touches.length !== 4) {
        this.pendingFour = null
        this.pendingPreview = []
        this.clearCanvas()
        return
      }
      const rect = host.getBoundingClientRect()
      const pts = []
      for (let i = 0; i < 4; i++) {
        const t = e.touches[i]
        const x = t.clientX - rect.left
        const y = t.clientY - rect.top
        if (this.isActiveFixedCs) {
          pts.push([
            Math.round((x / rect.width) * STD_W),
            Math.round((y / rect.height) * STD_H)
          ])
        } else {
          pts.push([
            Math.round((x / rect.width) * NORM),
            Math.round((y / rect.height) * NORM)
          ])
        }
      }
      this.pendingFour = pts
      this.pendingPreview = pts
      this.drawFourMarks(e.touches, rect)
    },
    onTouchEnd(e) {
      if (e.touches && e.touches.length > 0) return
      const pts = this.pendingFour
      this.pendingPreview = []
      if (!pts || pts.length !== 4) {
        this.pendingFour = null
        this.clearCanvas()
        return
      }
      this.pendingFour = null
      this.clearCanvas()
      if (Date.now() - this.lastSubmitAt < 2000) {
        Toast('2 秒内仅可提交一次，已忽略本次')
        return
      }
      this.submitPoints(pts)
    },
    async submitPoints(pts) {
      if (this.saving) return
      this.saving = true
      try {
        const res = await addSealPoints({
          ruleCode: this.selectedRuleCode,
          sealPoints: pts,
          coordinateSystem: Number(this.activeCoordinateSystem)
        })
        if (res && typeof res === 'object' && res.status !== undefined && Number(res.status) !== 200) {
          Toast.fail(res.msg || '录入失败')
          return
        }
        this.lastSubmitAt = Date.now()
        Toast.success('录入成功')
        this.entryPopupVisible = false
      } catch (err) {
        console.error(err)
        const ax = err && err.response && err.response.data
        const msg =
          (ax && (ax.msg || ax.message)) ||
          (err && err.message) ||
          '录入失败'
        Toast.fail(msg)
      } finally {
        this.saving = false
      }
    }
  }
}
</script>

<style scoped>
.seal-coord-page {
  min-height: 100vh;
  background: #f5f5f5;
  box-sizing: border-box;
}
.toolbar {
  padding: 12px 16px;
  background: #fff;
  margin: 12px;
  border-radius: 8px;
}
.form-row {
  margin-bottom: 12px;
}
.form-row label {
  display: block;
  font-size: 13px;
  color: #666;
  margin-bottom: 6px;
}
.rule-select {
  width: 100%;
}
.cs-radio-group {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px 20px;
  font-size: 14px;
}
.cs-radio-group >>> .el-radio {
  margin-right: 0;
}
.cs-hint {
  font-size: 12px;
  color: #1989fa;
  line-height: 1.5;
  margin: 0 0 8px;
  padding: 8px 10px;
  background: #ecf5ff;
  border-radius: 6px;
}
.tip {
  font-size: 12px;
  color: #999;
  line-height: 1.5;
  margin: 0 0 16px;
}
.start-btn {
  margin-top: 4px;
}

/* 弹层外壳 */
.entry-shell {
  display: flex;
  flex-direction: column;
  background: #fff;
  overflow: hidden;
}
.entry-shell--800 {
  width: min(800px, 92vw);
  max-width: 800px;
  border-radius: 10px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.12);
}
.entry-shell--full {
  position: relative;
  width: 100vw;
  height: 100vh;
  height: 100dvh;
  max-height: -webkit-fill-available;
  border-radius: 0;
  padding: 0;
  margin: 0;
}
.entry-hd {
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 10px 12px;
  padding-top: calc(10px + env(safe-area-inset-top, 0px));
  border-bottom: 1px solid #ebedf0;
  background: #fff;
}
.entry-title {
  font-size: 15px;
  font-weight: 600;
  color: #323233;
}

.touch-host {
  position: relative;
  touch-action: none;
  box-sizing: border-box;
  background: #fafafa;
}
.touch-host--800 {
  width: 100%;
  aspect-ratio: 800 / 600;
  max-height: min(70vh, 520px);
  border: 2px dashed #c8c9cc;
}
.touch-host--full {
  position: absolute;
  left: 0;
  top: 0;
  right: 0;
  bottom: 0;
  width: 100%;
  height: 100%;
}
.entry-float-close-wrap {
  position: absolute;
  top: calc(8px + env(safe-area-inset-top, 0px));
  right: calc(8px + env(safe-area-inset-right, 0px));
  z-index: 20;
}
.entry-float-close {
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.12);
}
.entry-float-close >>> .van-button {
  background: rgba(255, 255, 255, 0.94) !important;
}

.region-label {
  position: absolute;
  left: 10px;
  right: 10px;
  bottom: max(10px, env(safe-area-inset-bottom, 0px));
  z-index: 1;
  font-size: 11px;
  color: #646566;
  line-height: 1.4;
  pointer-events: none;
  text-align: center;
}
.region-label--cs2 {
  color: #1989fa;
}
.mark-canvas {
  position: absolute;
  left: 0;
  top: 0;
  width: 100%;
  height: 100%;
  pointer-events: none;
}
.preview-text {
  position: absolute;
  bottom: max(40px, calc(env(safe-area-inset-bottom, 0px) + 32px));
  left: 0;
  right: 0;
  text-align: center;
  font-size: 14px;
  color: #e53935;
  font-weight: 600;
  pointer-events: none;
}
</style>

<style>
/* 全屏弹层：去掉圆角与内边距，保证真正铺满可视区域 */
.entry-popup--fullsheet.van-popup {
  border-radius: 0 !important;
}
.entry-popup--fullsheet.van-popup .van-popup__content {
  width: 100%;
  height: 100%;
  padding: 0 !important;
  margin: 0 !important;
}
</style>
